.stateScreen {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  font-size: 16px;
  color: var(--text-muted);
}

.page {
  min-height: 100vh;
}

.body {
  max-width: var(--content-max);
  margin: 0 auto;
  padding: 0 16px 60px;
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.bannerWrap {
  padding-top: 16px;
}

/* Pulls the card up so its top overlaps the hero's dark bottom edge, per Main.dc.html - sits
   above the tab bar, not inside a tab's content, so it stays visible on every tab.
   Two variants, chosen in App.tsx by whether the event has a flyer image:
   - WITH a flyer, the flyer's fixed height reliably makes the hero tall regardless of title
     length, so a larger, more dramatic overlap is safe (mirrors the mockup's own values).
   - WITHOUT a flyer, the hero's height is governed entirely by the (variable-length) title
     text, so the overlap is kept smaller than Hero's own padding-bottom (48px/56px, see
     Hero.module.css) - that padding is guaranteed blank space after the content no matter how
     many lines the title wraps to, so this amount can never collide with the title/host line.
     Confirmed live: a real flyer-less event with a 3-line title collided with the old
     one-size-fits-all -44px overlap. */
.mobileRegWrap {
  margin: -44px 16px 0;
  position: relative;
  z-index: 2;
}
.mobileRegWrapCompact {
  margin: -32px 16px 0;
  position: relative;
  z-index: 2;
}

.tabBar {
  display: flex;
  gap: 4px;
  border-bottom: 1px solid var(--border-1);
  position: sticky;
  top: 0;
  background: var(--bg-page);
  z-index: 10;
}

.tab {
  flex: 1;
  padding: 14px 8px;
  border: none;
  background: transparent;
  font-weight: 600;
  font-size: 14px;
  color: var(--text-muted);
  border-bottom: 3px solid transparent;
}

.tabActive {
  color: var(--text-primary);
  border-bottom-color: var(--accent);
}

.anchorNav {
  display: flex;
  gap: 6px;
  border-bottom: 1px solid var(--border-1);
}
.anchorNav a {
  padding: 16px 12px;
  font-weight: 600;
  font-size: 16px;
  color: var(--text-muted);
  text-decoration: none;
  border-bottom: 3px solid transparent;
}

.main {
  display: flex;
  flex-direction: column;
  gap: 32px;
  padding-top: 16px;
}

.section {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

/* Mobile Classes tab: groups the Classes section with Add-ons right under it, so the sticky
   footer bar's bottom-clearance padding (added only when there's something unpaid to check out -
   a class entry OR an add-on) lands on whichever one ends up last, same 32px gap .main gives its
   own top-level children. */
.classesTabGroup {
  display: flex;
  flex-direction: column;
  gap: 32px;
}

.heading {
  margin: 0;
  font-family: var(--font-display);
  font-weight: 700;
  font-size: 24px;
  text-transform: uppercase;
}

.about {
  font-size: 16px;
  line-height: 1.6;
  color: var(--text-secondary);
}
.about p {
  margin: 0 0 12px;
}
.about p:last-child {
  margin-bottom: 0;
}

.rail {
  display: none;
}

@media (min-width: 1200px) {
  .body {
    padding: 0 60px 80px;
  }
  .desktopGrid {
    display: flex;
    gap: 40px;
    align-items: flex-start;
    /* Centers the (capped-width) row instead of letting it hug the left edge with unused space
       to the right of the rail on wide screens - see .main's comment. */
    justify-content: center;
  }
  .main {
    flex: 1;
    min-width: 0;
    /* Fixed cap, matching Desktop.dc.html's own hard 780px content column (not computed from the
       rail's width) - same reasoning as Hero.module.css's .details: letting this stretch to fill
       whatever space is left before the rail made class rows/schedule cards far wider and more
       spread out than the mockup, especially on a large monitor. flex: 1 still lets it shrink
       gracefully below 780px on a narrower desktop viewport. */
    max-width: 780px;
  }
  .rail {
    display: flex;
    flex-direction: column;
    gap: 16px;
    width: var(--rail-w);
    flex-shrink: 0;
    position: sticky;
    top: 20px;
    z-index: 2;
    /* Aligns the card's TOP with the flyer image's TOP (not a smaller, more conservative overlap -
       per explicit feedback, the card should read as starting level with the flyer, the way
       Desktop.dc.html's own `margin-top: -440px` does). Computed, not guessed: with a flyer,
       hero height is reliably padding-top(36) + flyerButton height (280 * 4/3 = 373.3) +
       padding-bottom(56) = 465.3px, and the flyer itself starts 36px below the hero's top - so
       pulling the card up by (465.3 - 36) lands its top at that same 36px. Safe against title
       length because Hero.module.css's `.details` now reserves the rail's exact footprint (see
       its comment) rather than sharing space with it. See .railCompact for the no-flyer case,
       where there's no flyer height to make hero height predictable this way. */
    margin-top: -429px;
  }
  .railCompact {
    margin-top: -40px;
  }
}
